import { Link, useLocation, useNavigate } from "react-router-dom";
import { Zap, AlertTriangle } from "lucide-react";
import { useLenis, scrollToSection } from "@/App";

const NAV = [
    { id: "home", label: "Home", testId: "footer-link-home" },
    { id: "subscription", label: "Subscription Plan", testId: "footer-link-subscription" },
    { id: "records", label: "Records", testId: "footer-link-records" },
    { id: "contact", label: "Contact Us", testId: "footer-link-contact" },
];

const LEGAL = [
    { to: "/terms", label: "Terms & Conditions", testId: "footer-link-terms" },
    { to: "/privacy-policy", label: "Privacy Policy", testId: "footer-link-privacy" },
    { to: "/disclaimer", label: "Disclaimer", testId: "footer-link-disclaimer" },
];

export const Footer = () => {
    const lenisRef = useLenis();
    const navigate = useNavigate();
    const location = useLocation();

    const go = (id) => {
        if (location.pathname !== "/") navigate(`/#${id}`);
        else scrollToSection(lenisRef.current, id);
    };

    return (
        <footer data-testid="footer-container" className="relative border-t border-white/5 bg-[#05070A]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
                <div className="grid md:grid-cols-3 gap-12">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-lg bg-bn-blue/15 border border-bn-blue/40 flex items-center justify-center">
                                <Zap className="w-4 h-4 text-bn-blue" />
                            </span>
                            <span className="font-display font-extrabold tracking-tight text-lg text-white">
                                BETTING<span className="text-bn-blue">NATOR</span>
                            </span>
                        </div>
                        <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xs">
                            Professional Football Betting Analysis & Daily Doubles.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-8">
                        <div>
                            <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 mb-4">NAVIGATE</div>
                            <ul className="space-y-2.5">
                                {NAV.map((l) => (
                                    <li key={l.id}>
                                        <button data-testid={l.testId} onClick={() => go(l.id)} className="text-sm text-slate-400 hover:text-bn-blue transition-colors">
                                            {l.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 mb-4">LEGAL</div>
                            <ul className="space-y-2.5">
                                {LEGAL.map((l) => (
                                    <li key={l.to}>
                                        <Link data-testid={l.testId} to={l.to} className="text-sm text-slate-400 hover:text-bn-blue transition-colors">
                                            {l.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 mb-4">RESPONSIBLE BETTING</div>
                        <div data-testid="footer-responsible-gambling-badge" className="flex items-start gap-3 rounded-xl border border-bn-line bg-bn-card/50 p-4">
                            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Betting involves financial risk and can be addictive. Past results do not guarantee future results. Nothing on this website constitutes financial advice or a guarantee of profit. 18+ only. Never bet more than you can afford to lose.
                            </p>
                        </div>
                        <div className="mt-4 flex gap-2">
                            <span className="font-mono text-[10px] font-bold px-3 py-1.5 rounded-full border border-white/10 text-slate-400">18+ ONLY</span>
                            <span className="font-mono text-[10px] font-bold px-3 py-1.5 rounded-full border border-white/10 text-slate-400">BET RESPONSIBLY</span>
                        </div>
                    </div>
                </div>
                <div className="mt-14 text-center">
                    <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 mb-4">PROMO BANNER — RIGHT-CLICK AND “SAVE IMAGE AS…” TO DOWNLOAD</div>
                    <img
                        src="/promo-banner.gif"
                        alt="BettingNator Info — Single & Double Football Prediction"
                        data-testid="footer-promo-banner"
                        className="mx-auto w-full max-w-[728px] rounded-2xl hover:shadow-[0_0_40px_rgba(0,102,255,0.35)] transition-shadow duration-500"
                    />
                </div>
                <div className="mt-14 pt-7 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="font-mono text-[11px] text-slate-600">© 2024 BETTINGNATOR. ALL RIGHTS RESERVED.</p>
                    <p className="font-mono text-[11px] text-slate-600">ANALYSIS & INFORMATION — NOT FINANCIAL ADVICE</p>
                </div>
            </div>
        </footer>
    );
};
